<template>
  <div class="com_bg1">
    <div id="wrapper1">
      <img :src="logoImage" :alt="altText" class="with-kaunet_logo2" />
      <div class="com_form_contet1">
        <div class="inner1">
          <h2 class="title1">ワンタイムパスワード</h2>
          <p class="com_center1 com_mgb30">
            メールに記載のコードを入力してください。
          </p>
          <div class="form1 tp1 com_mgb40">
            <div class="group1">
              <AlphanumericInput
                id="otp"
                v-model="otp"
                type="text"
                maxlength="6"
              />
            </div>
            <div
              v-if="errorMessage"
              class="error-message"
              style="margin-left: 140px"
            >
              {{ errorMessage }}
            </div>
            <button type="submit" @click="submitOtp" :disabled="isProcessing">
              ログイン
            </button>
          </div>
        </div>
      </div>
      <p class="com_copyright1">© 2025 Kaunet</p>
    </div>
  </div>
</template>

<script lang="ts">
import { AxiosError } from "axios";
import { useRouter, useRoute } from "vue-router";
import { defineComponent, ref, PropType, computed } from "vue";
import messages from "../../assets/messages.json";
import {
  formatMessage,
  outputLoginDebugLogs,
  setUserInfo,
  sanitizeInput,
  getLoginInfomation,
  outputDebugLogs,
} from "@/Common";
import { AppConstants } from "@/constants/AppConstants";
import AlphanumericInput from "../controls/AlphanumericInput.vue";
import apiClient from "@/util/apiClient";

export default defineComponent({
  name: "OtpAuthCOmponent",
  props: {
    loginId: {
      type: String as PropType<string>,
      required: true,
    },
    password: {
      type: String as PropType<string>,
      required: true,
    },
  },
  components: {
    AlphanumericInput,
  },
  setup() {
    const otp = ref("");
    const errorMessage = ref(""); // errorMessage をリアクティブなデータとして定義
    const router = useRouter();
    const route = useRoute();
    const isProcessing = ref(false);
    const loginId = computed(() => route.query.loginId as string);
    const password = computed(() => route.query.password as string);

    // --- システム区分に応じてロゴを切り替え ---
    const systemKbn = process.env.VUE_APP_SYSTEM_KBN;
    const logoImage =
      process.env.VUE_APP_DIR + "/images/" + systemKbn + "/logo2.png";

    const STYLE_ALT_MAP: Record<string, string> = {
      "01": "ウィズカウネット エージェントポータル",
      "02": "ベースカウネット エージェントポータル",
      "03": "サプライヤーポータル",
    };

    const altText = STYLE_ALT_MAP[systemKbn];

    // ***** 入力チェック *****
    const validateInput = (): boolean => {
      const sanitizedOtp = sanitizeInput(otp.value);
      if (!sanitizedOtp) {
        errorMessage.value = formatMessage(
          messages.ERR_0001,
          "セキュリティコード"
        );
        return false;
      } else {
        errorMessage.value = ""; // エラーがない場合はクリア
        return true;
      }
    };

    // ***** ワンタイムパスワード認証 *****
    const submitOtp = async () => {
      isProcessing.value = true;
      try {
        // 入力チェック
        if (!validateInput()) {
          return;
        }

        outputDebugLogs("ユーザーID: " + loginId.value);
        outputDebugLogs("パスワード: " + password.value);

        // OTP認証API
        const response = await apiClient.post("/api/Auth/OtpLogin", {
          systemKbn: process.env.VUE_APP_SYSTEM_KBN,
          loginId: loginId.value,
          password: password.value,
          otp: sanitizeInput(otp.value),
        });
        localStorage.setItem(
          AppConstants.TOKEN_SESSION_NAME,
          response.data.token
        );
        localStorage.setItem(
          AppConstants.SYSTEMDATE_SESSION_NAME,
          response.data.systemDate
        );

        outputDebugLogs("OTP認証成功");
        outputDebugLogs("agCode: " + response.data.user.agCode);
        outputDebugLogs("userId: " + response.data.user.userId);

        // ログイン時のグローバルナビや権限情報などを取得
        const error = await getLoginInfomation(
          response.data.user.agCode,
          response.data.user.userId,
          false
        );

        if (error) {
          errorMessage.value = error;
          return;
        } else {
          errorMessage.value = "";
        }

        // レスポンス情報のユーザー情報をローカルストレージへ格納
        setUserInfo(response);

        // デバックログ出力
        outputLoginDebugLogs(response.data.user);

        // ベースログインフラグがTrueの場合、新規タブを開いてベースログインAPIを呼び出す
        outputDebugLogs(
          "ベースログインフラグ" + String(response.data.user.isBaseLogin)
        );
        if (response.data.user.isBaseLogin == true) {
          const url = formatMessage(
            process.env.VUE_APP_BASE_LOGIN_URL,
            process.env.VUE_APP_SYSTEM_KBN,
            response.data.user.agCode,
            response.data.user.userId,
            response.data.user.baseAgCode,
            response.data.token
          );

          outputDebugLogs("ベースURL:" + url);
          const newTab = window.open(url, "_blank");
          if (newTab) {
            // 新しいウィンドウのロードイベントにフォーカス戻しを設定
            newTab.onload = () => {
              setTimeout(() => {
                newTab.blur();
                window.focus();
              }, 500); // 0.5秒遅延させる
            };
          }
        }

        // 認証成功の場合は、ポータルトップへ
        router.push("/portal-top");
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        if (axiosError.response) {
          switch (axiosError.response.status) {
            case 401:
              errorMessage.value = messages.ERR_USER004;
              break;
            case 500:
              errorMessage.value = messages.ERR_SYS999;
              break;
            default:
              errorMessage.value = messages.ERR_SYS001;
          }
        } else if (axiosError.request) {
          errorMessage.value = messages.ERR_SYS001;
        } else {
          errorMessage.value = messages.ERR_SYS002;
          if (process.env.VUE_APP_DEBUG === "true") {
            console.log("Error: ", axiosError.message);
          }
        }
      } finally {
        isProcessing.value = false;
      }
    };

    return {
      otp,
      submitOtp,
      errorMessage,
      validateInput,
      isProcessing,
      logoImage,
      altText,
    };
  },
  methods: {},
});
</script>
